const STORAGE_PREFIX = 'avalon_';
const DEFAULT_EXPIRY_HOURS = 24;

interface StorageData<T> {
  data: T;
  timestamp: number;
}

/**
 * 检查数据是否过期
 */
function isExpired(timestamp: number, expiryHours: number): boolean {
  const expiryMs = expiryHours * 60 * 60 * 1000;
  return Date.now() - timestamp > expiryMs;
}

/**
 * 保存数据到 localStorage
 */
export function saveToStorage<T>(key: string, data: T, _expiryHours: number = DEFAULT_EXPIRY_HOURS): void {
  try {
    const storageKey = `${STORAGE_PREFIX}${key}`;
    const storageData: StorageData<T> = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(storageKey, JSON.stringify(storageData));
  } catch (error) {
    console.error(`Failed to save to localStorage [${key}]:`, error);
  }
}

/**
 * 从 localStorage 读取数据
 * @returns 数据或 null（如果不存在或已过期）
 */
export function loadFromStorage<T>(key: string, expiryHours: number = DEFAULT_EXPIRY_HOURS): T | null {
  try {
    const storageKey = `${STORAGE_PREFIX}${key}`;
    const item = localStorage.getItem(storageKey);
    
    if (!item) return null;
    
    const storageData: StorageData<T> = JSON.parse(item);
    
    // 检查是否过期
    if (isExpired(storageData.timestamp, expiryHours)) {
      // 清理过期数据
      localStorage.removeItem(storageKey);
      return null;
    }
    
    return storageData.data;
  } catch (error) {
    console.error(`Failed to load from localStorage [${key}]:`, error);
    return null;
  }
}

/**
 * 从 localStorage 删除数据
 */
export function removeFromStorage(key: string): void {
  try {
    const storageKey = `${STORAGE_PREFIX}${key}`;
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error(`Failed to remove from localStorage [${key}]:`, error);
  }
}

/**
 * 清理所有过期的阿瓦隆相关数据
 */
export function cleanExpiredStorage(): void {
  try {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        try {
          const item = localStorage.getItem(key);
          if (item) {
            const storageData: StorageData<unknown> = JSON.parse(item);
            if (isExpired(storageData.timestamp, DEFAULT_EXPIRY_HOURS)) {
              keysToRemove.push(key);
            }
          }
        } catch {
          // 解析失败，标记为删除
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Failed to clean expired storage:', error);
  }
}

// Storage keys
export const STORAGE_KEYS = {
  GAME_RECORD: 'game_record_current',
  GAME_RECORD_DRAFT: 'game_record_draft',
  CARD_DEAL: 'card_deal_config',
  USER_PROFILE: 'user_profile',
} as const;

/**
 * 保存用户配置（昵称、头像等）
 */
export function saveUserProfile(nickname: string, avatarColor?: string): void {
  try {
    localStorage.setItem('avalon_user_nickname', nickname.trim());
    if (avatarColor) {
      localStorage.setItem('avalon_user_avatar', avatarColor);
    }
  } catch (error) {
    console.error('Failed to save user profile:', error);
  }
}

/**
 * 获取用户昵称
 */
export function getUserNickname(): string | null {
  return localStorage.getItem('avalon_user_nickname');
}

/**
 * 获取用户头像颜色
 */
export function getUserAvatar(): string | null {
  return localStorage.getItem('avalon_user_avatar');
}

/**
 * 获取完整的用户配置
 */
export function getUserProfile(): { nickname: string; avatar?: string } | null {
  const nickname = getUserNickname();
  if (!nickname) return null;
  
  return {
    nickname,
    avatar: getUserAvatar() || undefined,
  };
}

/**
 * 清除用户配置
 */
export function clearUserProfile(): void {
  localStorage.removeItem('avalon_user_nickname');
  localStorage.removeItem('avalon_user_avatar');
}
